import React from 'react'
import { Card, CardContent } from '@agion/ui'
import { cn } from '@/core/analytics_utils'

export interface StatCardProps {
  icon: React.ComponentType<{ className?: string }>
  label: string
  value: string
  subtext?: string
  /** Tailwind background class for the icon tile (e.g. "bg-blue-500"). */
  color: string
  /** Optional override for the value text color (defaults to neutral slate). */
  valueClassName?: string
  /** When set, the card becomes a keyboard-accessible button. */
  onClick?: () => void
  /** Tooltip / accessible title (pass a translated string). */
  title?: string
}

/**
 * Shared metric summary card: an icon tile plus label / value / optional subtext.
 * Shared presentation for overview statistics.
 */
export const StatCard: React.FC<StatCardProps> = ({
  icon: Icon, label, value, subtext, color, valueClassName, onClick, title,
}) => {
  const clickable = onClick !== undefined
  return (
    <Card
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={clickable
        ? (e: React.KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick() }
          }
        : undefined}
      title={title}
      className={clickable ? 'cursor-pointer transition-colors hover:bg-slate-50 dark:hover:bg-slate-700/20' : undefined}
    >
      <CardContent className="flex items-center gap-4 py-4">
        <div className={`rounded-lg p-2.5 ${color}`}>
          <Icon className="h-5 w-5 text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm text-slate-500 dark:text-slate-400">{label}</p>
          <p className={cn('text-2xl font-semibold', valueClassName ?? 'text-slate-900 dark:text-slate-100')}>{value}</p>
          {subtext && <p className="text-xs text-slate-400 dark:text-slate-500">{subtext}</p>}
        </div>
      </CardContent>
    </Card>
  )
}
